import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { FollowButton } from '../components/FollowButton';
import { MessageButton } from '../components/MessageButton';
import BookingModal from './BookingModal';
import ServiceCard from './ServiceCard';
import { API } from '../config/api';
import {
  Building2, MapPin, Star, Share2, Users, Verified,
  Wifi, Mic2, Music, Speaker, FileText, Image,
  Clock, DollarSign, Loader2, Phone, Globe,
  CheckCircle, Calendar, Maximize
} from 'lucide-react';

export default function StudioProfileView({ profile, roleProfile, isOwnProfile, followerCount, followingCount, isFollowing, onFollowChange }) {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('services');
  const [loadingContent, setLoadingContent] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const rp = roleProfile || {};
  const studioName = rp.studio_name || rp.name || profile.name;
  const bio = rp.bio || rp.description || profile.bio;
  const photos = rp.photos || rp.gallery || [];
  const amenities = rp.amenities || rp.services || [];
  const gear = rp.gear || rp.equipment || [];
  const hourlyRate = rp.hourly_rate || rp.price_range || profile.rate_range;
  const address = rp.address || rp.neighborhood || profile.neighborhood || profile.city || profile.location;

  useEffect(() => {
    fetchContent();
  }, [profile.user_id]);

  const fetchContent = async () => {
    setLoadingContent(true);
    try {
      const [svcRes, postRes] = await Promise.all([
        fetch(`${API}/services/user/${profile.user_id}`),
        fetch(`${API}/posts?user_id=${profile.user_id}&limit=20`),
      ]);
      if (svcRes.ok) { const d = await svcRes.json(); setServices(d.services || []); }
      if (postRes.ok) { const d = await postRes.json(); setPosts(Array.isArray(d) ? d : d.posts || []); }
    } catch (e) { console.error('Content fetch error:', e); }
    setLoadingContent(false);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/u/${profile.handle}`;
    try { await navigator.clipboard.writeText(url); toast.success('Studio link copied!'); }
    catch { toast.error('Could not copy link'); }
  };

  const amenityIcons = { booth: Mic2, equipment: Speaker, instruments: Music, wifi: Wifi, 'space size': Maximize };

  return (
    <div className="min-h-screen" data-testid="studio-profile">
      {/* Banner — larger for studios, visual-heavy */}
      <div className="relative h-56 md:h-80 overflow-hidden">
        {rp.banner_url || profile.banner_url ? (
          <img src={rp.banner_url || profile.banner_url} alt="" className="w-full h-full object-cover" />
        ) : photos.length > 0 ? (
          <img src={photos[0]} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-indigo-900/40 via-zinc-900 to-indigo-800/30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
      </div>

      {/* Profile Header */}
      <div className="max-w-5xl mx-auto px-4 -mt-20 relative z-10">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          {/* Logo/Avatar */}
          <div className="w-28 h-28 rounded-2xl border-4 border-background overflow-hidden bg-muted flex-shrink-0 shadow-xl">
            {(rp.avatar_url || profile.avatar_url || profile.picture) ? (
              <img src={rp.avatar_url || profile.avatar_url || profile.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Building2 className="w-10 h-10 text-indigo-500/50" /></div>
            )}
          </div>

          {/* Info + Actions */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-2xl md:text-3xl font-bold">{studioName}</h1>
              {(rp.is_verified || profile.is_verified) && <Verified className="w-5 h-5 text-indigo-400" />}
              <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30">Studio</Badge>
            </div>
            <p className="text-muted-foreground text-sm">{profile.handle ? `@${profile.handle}` : ""}</p>

            {/* Location + Rate */}
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              {address && (
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{address}</span>
              )}
              {hourlyRate && (
                <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{typeof hourlyRate === 'number' ? `$${hourlyRate}/hr` : hourlyRate}</span>
              )}
              {rp.rating > 0 && (
                <span className="flex items-center gap-1"><Star className="w-3 h-3 text-yellow-500" />{rp.rating.toFixed(1)}</span>
              )}
            </div>

            {/* Amenities */}
            {amenities.length > 0 && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {amenities.map(a => {
                  const label = typeof a === 'string' ? a : a.name || a;
                  const Icon = amenityIcons[label.toLowerCase()] || CheckCircle;
                  return (
                    <Badge key={label} variant="outline" className="bg-indigo-500/10 border-indigo-500/20 text-indigo-300 text-xs">
                      <Icon className="w-3 h-3 mr-1" />{label}
                    </Badge>
                  );
                })}
              </div>
            )}

            {/* Stats */}
            <div className="flex items-center gap-5 mt-3">
              <div className="text-center"><p className="font-bold text-lg">{followerCount}</p><p className="text-xs text-muted-foreground">Followers</p></div>
              <div className="text-center"><p className="font-bold text-lg">{followingCount}</p><p className="text-xs text-muted-foreground">Following</p></div>
              <div className="text-center"><p className="font-bold text-lg">{rp.credits_count || rp.booking_count || 0}</p><p className="text-xs text-muted-foreground">Sessions</p></div>
              <div className="text-center"><p className="font-bold text-lg">{services.length}</p><p className="text-xs text-muted-foreground">Services</p></div>
            </div>

            {/* Action Buttons */}
            {!isOwnProfile && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <FollowButton targetUserId={profile.user_id} initialIsFollowing={isFollowing} onFollowChange={onFollowChange} />
                <MessageButton targetUserId={profile.user_id} targetName={studioName} variant="outline" size="default" showText />
                <Button onClick={() => setShowBooking(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="book-studio-btn">
                  <Calendar className="w-4 h-4 mr-2" />Book Studio
                </Button>
                <Button variant="ghost" size="icon" onClick={handleShare} data-testid="share-studio-btn"><Share2 className="w-4 h-4" /></Button>
              </div>
            )}
            {isOwnProfile && (
              <div className="flex items-center gap-2 mt-4">
                <Button variant="outline" onClick={() => navigate('/dashboard/studio')} data-testid="edit-studio-profile-btn">
                  <Building2 className="w-4 h-4 mr-2" />Manage Studio
                </Button>
                <Button variant="ghost" size="icon" onClick={handleShare}><Share2 className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        {bio && <p className="mt-5 text-sm text-muted-foreground max-w-2xl leading-relaxed">{bio}</p>}

        {/* Photo Gallery (inline small strip) */}
        {photos.length > 0 && (
          <div className="mt-5">
            <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2"><Image className="w-4 h-4" />Studio Photos</h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {photos.map((url, i) => (
                <img
                  key={i} src={url} alt={`Studio ${i + 1}`}
                  className="w-32 h-24 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                  onClick={() => setSelectedPhoto(url)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Equipment */}
        {gear.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2"><Speaker className="w-4 h-4" />Equipment</h3>
            <div className="flex flex-wrap gap-2">
              {gear.map((g, i) => <Badge key={i} variant="outline" className="text-xs bg-muted/20">{g}</Badge>)}
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="w-full justify-start bg-muted/30 p-1 mb-6 overflow-x-auto">
            <TabsTrigger value="services" className="flex items-center gap-2" data-testid="studio-tab-services">
              <Building2 className="w-4 h-4" />Services{services.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{services.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="posts" className="flex items-center gap-2" data-testid="studio-tab-posts">
              <FileText className="w-4 h-4" />Posts{posts.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{posts.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="photos" className="flex items-center gap-2" data-testid="studio-tab-photos">
              <Image className="w-4 h-4" />Gallery
            </TabsTrigger>
          </TabsList>

          <TabsContent value="services">
            {loadingContent ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : services.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No services listed</h3>
                <p className="text-muted-foreground">{isOwnProfile ? 'Add studio services from your dashboard.' : 'This studio hasn\'t listed any services yet.'}</p>
                {isOwnProfile && <Button className="mt-4" onClick={() => navigate('/dashboard/studio')}>Add Services</Button>}
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {services.map(svc => <ServiceCard key={svc.service_id} service={svc} onBook={!isOwnProfile ? () => setShowBooking(true) : null} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="posts">
            {posts.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No posts yet</h3>
                <p className="text-muted-foreground">{isOwnProfile ? 'Share your studio sessions!' : 'No posts from this studio yet.'}</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map(post => (
                  <Card key={post.post_id} className="glass-card border-border p-4 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => navigate(`/post/${post.post_id}`)}>
                    <p className="text-sm">{post.text || post.content}</p>
                    {post.media_url && <img src={post.media_url} alt="" className="mt-3 rounded-lg max-h-64 object-cover" />}
                    <p className="text-xs text-muted-foreground mt-2">{new Date(post.created_at).toLocaleDateString()}</p>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="photos">
            {photos.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No studio photos yet</h3>
                {isOwnProfile && <p className="text-muted-foreground">Upload photos from your dashboard to showcase your space.</p>}
              </Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {photos.map((url, i) => (
                  <img key={i} src={url} alt={`Studio ${i+1}`} className="w-full h-48 object-cover rounded-xl cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setSelectedPhoto(url)} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-50 bg-background/90 flex items-center justify-center p-4" onClick={() => setSelectedPhoto(null)}>
          <img src={selectedPhoto} alt="" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
        </div>
      )}

      {/* Booking Modal */}
      {showBooking && (
        <BookingModal
          targetId={profile.user_id}
          targetName={studioName}
          targetRole="studio"
          services={services}
          onClose={() => setShowBooking(false)}
        />
      )}
    </div>
  );
}
